import React, { useState, useEffect, useCallback } from 'react';
import { Search, MessageSquare, ThumbsUp, User, Calendar, AlertCircle, Loader2, Info, ExternalLink, Database, RefreshCw, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Alert, AlertDescription } from '../ui/alert';
import { Switch } from '../ui/switch';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_COLORS = {
  youtube: 'bg-red-500/20 text-red-400 border-red-500/30',
  tiktok: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  bilibili: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  douyin: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  xiaohongshu: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  weibo: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  instagram: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

const PLATFORM_LABELS = {
  youtube: 'YouTube',
  tiktok: 'TikTok',
  bilibili: 'Bilibili',
  douyin: 'Douyin',
  xiaohongshu: 'Xiaohongshu',
  weibo: 'Weibo',
  instagram: 'Instagram',
};

export default function CommentSearchPage({ lang = 'en' }) {
  const { isAdmin } = useAuth();
  const [keyword, setKeyword] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [supportedPlatforms, setSupportedPlatforms] = useState([]);
  const [unsupportedPlatforms, setUnsupportedPlatforms] = useState([]);
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);
  
  // Plan B: DB search mode
  const [useDbSearch, setUseDbSearch] = useState(false);
  const [dbStats, setDbStats] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const t = {
    en: {
      title: 'Comment Keyword Search',
      subtitle: 'Search comments across multiple platforms',
      searchPlaceholder: 'Enter keyword to search...',
      search: 'Search',
      searching: 'Searching...',
      platforms: 'Platforms',
      selectAll: 'Select All',
      supportedPlatforms: 'Supported Platforms',
      unsupportedNote: 'Not available',
      results: 'Results',
      noResults: 'No comments found matching your keyword.',
      stats: 'Search Statistics',
      postsSearched: 'Posts searched',
      commentsScanned: 'Comments scanned',
      matchesFound: 'Matches found',
      likes: 'likes',
      from: 'from',
      enterKeyword: 'Enter a keyword to search comments',
      // Plan B translations
      searchMode: 'Search Mode',
      realTimeSearch: 'Real-time API',
      dbSearch: 'Database (Faster)',
      dbSearchDescription: 'Search from locally stored comments. Sync first for fresh data.',
      syncComments: 'Sync Comments',
      syncing: 'Syncing...',
      clearDb: 'Clear DB',
      dbStatus: 'Database Status',
      totalInDb: 'Comments in DB',
      postsWithComments: 'Posts with Comments',
      lastSync: 'Last Sync',
      noDataInDb: 'No comments stored. Click "Sync Comments" first.',
    },
    ko: {
      title: '댓글 키워드 검색',
      subtitle: '여러 플랫폼의 댓글을 키워드로 검색합니다',
      searchPlaceholder: '검색할 키워드를 입력하세요...',
      search: '검색',
      searching: '검색 중...',
      platforms: '플랫폼',
      selectAll: '전체 선택',
      supportedPlatforms: '검색 가능 플랫폼',
      unsupportedNote: '지원 불가',
      results: '검색 결과',
      noResults: '키워드와 일치하는 댓글이 없습니다.',
      stats: '검색 통계',
      postsSearched: '검색한 포스트',
      commentsScanned: '스캔한 댓글',
      matchesFound: '일치 항목',
      likes: '좋아요',
      from: '출처',
      enterKeyword: '키워드를 입력하여 댓글을 검색하세요',
      // Plan B translations
      searchMode: '검색 모드',
      realTimeSearch: '실시간 API',
      dbSearch: '데이터베이스 (빠름)',
      dbSearchDescription: '로컬에 저장된 댓글에서 검색합니다. 최신 데이터를 위해 먼저 동기화하세요.',
      syncComments: '댓글 동기화',
      syncing: '동기화 중...',
      clearDb: 'DB 삭제',
      dbStatus: '데이터베이스 상태',
      totalInDb: 'DB 내 댓글',
      postsWithComments: '댓글 있는 포스트',
      lastSync: '마지막 동기화',
      noDataInDb: '저장된 댓글이 없습니다. "댓글 동기화"를 먼저 클릭하세요.',
    },
    zh: {
      title: '评论关键词搜索',
      subtitle: '跨多平台搜索评论',
      searchPlaceholder: '输入关键词搜索...',
      search: '搜索',
      searching: '搜索中...',
      platforms: '平台',
      selectAll: '全选',
      supportedPlatforms: '可搜索平台',
      unsupportedNote: '不可用',
      results: '搜索结果',
      noResults: '未找到与关键词匹配的评论。',
      stats: '搜索统计',
      postsSearched: '搜索的帖子',
      commentsScanned: '扫描的评论',
      matchesFound: '匹配项',
      likes: '点赞',
      from: '来源',
      enterKeyword: '输入关键词搜索评论',
      searchMode: '搜索模式',
      realTimeSearch: '实时API',
      dbSearch: '数据库（更快）',
      dbSearchDescription: '从本地存储的评论中搜索。需要先同步获取最新数据。',
      syncComments: '同步评论',
      syncing: '同步中...',
      clearDb: '清空数据库',
      dbStatus: '数据库状态',
      totalInDb: '数据库中的评论',
      postsWithComments: '有评论的帖子',
      lastSync: '最近同步',
      noDataInDb: '暂无存储的评论。请先点击"同步评论"。',
    }
  }[lang] || {
    title: 'Comment Keyword Search',
    subtitle: 'Search comments across multiple platforms',
    searchPlaceholder: 'Enter keyword to search...',
    search: 'Search',
    searching: 'Searching...',
    platforms: 'Platforms',
    selectAll: 'Select All',
    supportedPlatforms: 'Supported Platforms',
    unsupportedNote: 'Not available',
    results: 'Results',
    noResults: 'No comments found matching your keyword.',
    stats: 'Search Statistics',
    postsSearched: 'Posts searched',
    commentsScanned: 'Comments scanned',
    matchesFound: 'Matches found',
    likes: 'likes',
    from: 'from',
    enterKeyword: 'Enter a keyword to search comments',
    searchMode: 'Search Mode',
    realTimeSearch: 'Real-time API',
    dbSearch: 'Database (Faster)',
    dbSearchDescription: 'Search from locally stored comments. Sync first for fresh data.',
    syncComments: 'Sync Comments',
    syncing: 'Syncing...',
    clearDb: 'Clear DB',
    dbStatus: 'Database Status',
    totalInDb: 'Comments in DB',
    postsWithComments: 'Posts with Comments',
    lastSync: 'Last Sync',
    noDataInDb: 'No comments stored. Click "Sync Comments" first.',
  };

  const fetchDbStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/comments/db-stats`);
      setDbStats(res.data);
    } catch (err) {
      console.error('Failed to fetch DB stats:', err);
      setDbStats(null);
    }
  }, []);

  useEffect(() => {
    fetchSupportedPlatforms();
    fetchDbStats();
  }, [fetchDbStats]);

  const fetchSupportedPlatforms = async () => {
    try {
      const res = await axios.get(`${API}/api/comment-search/supported-platforms`);
      setSupportedPlatforms(res.data.supported || []);
      setUnsupportedPlatforms(res.data.unsupported || []);
      setSelectedPlatforms(res.data.supported || []);
    } catch (err) {
      console.error('Failed to fetch supported platforms:', err);
      setSupportedPlatforms(['youtube', 'tiktok', 'bilibili', 'douyin', 'xiaohongshu']);
      setUnsupportedPlatforms(['weibo', 'instagram']);
      setSelectedPlatforms(['youtube', 'tiktok', 'bilibili', 'douyin', 'xiaohongshu']);
    }
  };

  const handleSearch = async () => {
    if (!keyword.trim()) return;
    
    setLoading(true);
    setError(null);
    setSearched(true);
    
    try {
      const endpoint = useDbSearch ? '/api/comments/search-db' : '/api/comment-search';
      const res = await axios.post(`${API}${endpoint}`, {
        keyword: keyword.trim(),
        platforms: selectedPlatforms,
        max_comments_per_post: 100,
        case_sensitive: false
      });
      
      setResults(res.data.results || []);
      setStats(res.data.stats || null);
      
      if (res.data.errors && res.data.errors.length > 0) {
        console.warn('Some platforms had errors:', res.data.errors);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err.response?.data?.detail || 'Search failed. Please try again.');
      setResults([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncComments = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/api/comments/sync`, {
        platforms: selectedPlatforms,
        max_comments_per_post: 100,
        clear_existing: false
      });
      
      toast.success(
        lang === 'ko' 
          ? `${res.data.stats.comments_synced}개 댓글 동기화 완료` 
          : lang === 'zh'
          ? `已同步 ${res.data.stats.comments_synced} 条评论`
          : `Synced ${res.data.stats.comments_synced} comments`
      );
      
      // Refresh DB stats
      await fetchDbStats();
    } catch (err) {
      console.error('Sync error:', err);
      toast.error(lang === 'ko' ? '동기화 실패' : lang === 'zh' ? '同步失败' : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleClearDb = async () => {
    if (!window.confirm(lang === 'ko' ? '모든 저장된 댓글을 삭제하시겠습니까?' : lang === 'zh' ? '确定要删除所有存储的评论吗？' : 'Clear all stored comments?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/api/comments/clear`);
      toast.success(lang === 'ko' ? 'DB 삭제 완료' : lang === 'zh' ? '数据库已清除' : 'Database cleared');
      await fetchDbStats();
      setResults([]);
      setStats(null);
    } catch (err) {
      console.error('Clear error:', err);
      toast.error(lang === 'ko' ? '삭제 실패' : lang === 'zh' ? '清除失败' : 'Clear failed');
    }
  };

  const handlePlatformToggle = (platform) => {
    setSelectedPlatforms(prev => 
      prev.includes(platform) 
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const handleSelectAll = () => {
    if (selectedPlatforms.length === supportedPlatforms.length) {
      setSelectedPlatforms([]);
    } else {
      setSelectedPlatforms([...supportedPlatforms]);
    }
  };

  const renderHighlight = (text) => {
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, i) => 
      i % 2 === 1 
        ? <span key={i} className="bg-yellow-500/30 text-yellow-200 px-0.5 rounded">{part}</span>
        : part
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString(lang === 'ko' ? 'ko-KR' : lang === 'zh' ? 'zh-CN' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Search className="h-6 w-6 text-primary" />
          {t.title}
        </h2>
        <p className="text-muted-foreground mt-1">{t.subtitle}</p>
      </div>

      {/* Platform Info Alert */}
      <Alert className="bg-blue-500/10 border-blue-500/30">
        <Info className="h-4 w-4 text-blue-400" />
        <AlertDescription className="text-blue-200">
          <span className="font-medium">{t.supportedPlatforms}:</span>{' '}
          {supportedPlatforms.map(p => PLATFORM_LABELS[p]).join(', ')}
          {unsupportedPlatforms.length > 0 && (
            <>
              <span className="mx-2">|</span>
              <span className="text-muted-foreground">
                {t.unsupportedNote}: {unsupportedPlatforms.map(p => PLATFORM_LABELS[p]).join(', ')}
              </span>
            </>
          )}
        </AlertDescription>
      </Alert>

      {/* Search Mode Toggle & DB Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Search Mode Selection */}
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="h-4 w-4" />
              {t.searchMode}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <p className="text-sm font-medium">
                  {useDbSearch ? t.dbSearch : t.realTimeSearch}
                </p>
                <p className="text-xs text-muted-foreground">
                  {useDbSearch ? t.dbSearchDescription : (lang === 'ko' ? '플랫폼 API에서 직접 검색합니다' : lang === 'zh' ? '直接从平台API搜索' : 'Search directly from platform APIs')}
                </p>
              </div>
              <Switch
                data-testid="search-mode-toggle"
                checked={useDbSearch}
                onCheckedChange={setUseDbSearch}
              />
            </div>
            
            {/* DB Sync Controls */}
            {useDbSearch && (
              <div className="flex gap-2 pt-2 border-t border-border/30">
                <Button
                  data-testid="sync-comments-btn"
                  variant="outline"
                  size="sm"
                  onClick={handleSyncComments}
                  disabled={syncing || selectedPlatforms.length === 0}
                  className="flex-1"
                >
                  {syncing ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      {t.syncing}
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-3 w-3 mr-1" />
                      {t.syncComments}
                    </>
                  )}
                </Button>
                {isAdmin && (
                <Button
                  data-testid="clear-db-btn"
                  variant="ghost"
                  size="sm"
                  onClick={handleClearDb}
                  disabled={syncing}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  {t.clearDb}
                </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* DB Status Card */}
        {useDbSearch && (
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t.dbStatus}</CardTitle>
            </CardHeader>
            <CardContent>
              {dbStats && dbStats.total_comments > 0 ? (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t.totalInDb}:</span>
                    <span className="font-medium">{dbStats.total_comments.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t.postsWithComments}:</span>
                    <span className="font-medium">{dbStats.posts_with_comments}</span>
                  </div>
                  {dbStats.latest_sync && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t.lastSync}:</span>
                      <span className="font-medium">{formatDate(dbStats.latest_sync)}</span>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1 pt-2 border-t border-border/30">
                    {Object.entries(dbStats.comments_by_platform || {}).map(([platform, count]) => (
                      <Badge key={platform} variant="outline" className={`text-xs ${PLATFORM_COLORS[platform]}`}>
                        {PLATFORM_LABELS[platform]}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t.noDataInDb}
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Search Form */}
      <Card className="bg-card border-border/50">
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Keyword Input */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="comment-search-input"
                  placeholder={t.searchPlaceholder}
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 bg-muted/50 border-border"
                />
              </div>
              <Button
                data-testid="comment-search-button"
                onClick={handleSearch}
                disabled={loading || !keyword.trim() || (selectedPlatforms.length === 0 && !useDbSearch) || (useDbSearch && (!dbStats || dbStats.total_comments === 0))}
                className="min-w-[100px]"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t.searching}
                  </>
                ) : (
                  <>
                    {useDbSearch ? <Database className="h-4 w-4 mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                    {t.search}
                  </>
                )}
              </Button>
            </div>

            {/* Platform Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">{t.platforms}</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleSelectAll}
                  className="text-xs"
                >
                  {t.selectAll}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {supportedPlatforms.map(platform => (
                  <label
                    key={platform}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-all ${
                      selectedPlatforms.includes(platform)
                        ? PLATFORM_COLORS[platform]
                        : 'bg-muted/30 text-muted-foreground border-border hover:bg-muted/50'
                    }`}
                  >
                    <Checkbox
                      checked={selectedPlatforms.includes(platform)}
                      onCheckedChange={() => handlePlatformToggle(platform)}
                      className="h-3.5 w-3.5"
                    />
                    <span className="text-sm">{PLATFORM_LABELS[platform]}</span>
                  </label>
                ))}
                {/* Show unsupported platforms as disabled */}
                {unsupportedPlatforms.map(platform => (
                  <div
                    key={platform}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full border bg-muted/10 text-muted-foreground/50 border-border/30 cursor-not-allowed"
                    title={t.unsupportedNote}
                  >
                    <span className="text-sm line-through">{PLATFORM_LABELS[platform]}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert className="bg-red-500/10 border-red-500/30">
          <AlertCircle className="h-4 w-4 text-red-400" />
          <AlertDescription className="text-red-200">{error}</AlertDescription>
        </Alert>
      )}

      {/* Search Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          {useDbSearch ? (
            <>
              <Card className="bg-card border-border/50">
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-foreground">{stats.total_comments_in_db?.toLocaleString() || 0}</p>
                  <p className="text-sm text-muted-foreground">{t.totalInDb}</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border/50">
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-primary">{stats.matches_found}</p>
                  <p className="text-sm text-muted-foreground">{t.matchesFound}</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border/50">
                <CardContent className="pt-4 text-center">
                  <Badge variant="outline" className="text-xs bg-green-500/20 text-green-400 border-green-500/30">
                    {t.dbSearch}
                  </Badge>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              <Card className="bg-card border-border/50">
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-foreground">{stats.total_posts_searched}</p>
                  <p className="text-sm text-muted-foreground">{t.postsSearched}</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border/50">
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-foreground">{stats.total_comments_scanned?.toLocaleString() || 0}</p>
                  <p className="text-sm text-muted-foreground">{t.commentsScanned}</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border/50">
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-primary">{stats.matches_found}</p>
                  <p className="text-sm text-muted-foreground">{t.matchesFound}</p>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Results */}
      {searched && (
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              {t.results} {results.length > 0 && `(${results.length})`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {results.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>{t.noResults}</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                {results.map((result, i) => (
                  <div
                    key={result.comment_id || i}
                    data-testid={`comment-result-${i}`}
                    className="p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors border border-border/30"
                  >
                    {/* Comment Header */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`${PLATFORM_COLORS[result.platform]} text-xs`}>
                          {PLATFORM_LABELS[result.platform]}
                        </Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {result.author}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <ThumbsUp className="h-3 w-3" />
                          {result.likes.toLocaleString()} {t.likes}
                        </span>
                        {result.published_at && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(result.published_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Comment Text with Highlight */}
                    <p className="text-sm text-foreground leading-relaxed">
                      {renderHighlight(result.match_highlight)}
                    </p>
                    
                    {/* Post Source */}
                    <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
                      <ExternalLink className="h-3 w-3" />
                      {t.from}: {result.post_title}...
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Initial State */}
      {!searched && !loading && (
        <div className="text-center py-12 text-muted-foreground">
          <Search className="h-16 w-16 mx-auto mb-4 opacity-20" />
          <p className="text-lg">{t.enterKeyword}</p>
        </div>
      )}
    </div>
  );
}
